import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { Box3, Vector3 } from 'three'

import type { OrbitControlsRefProps } from '../controls/orbit-controls'
import type { ModelControlsInterface } from '../objects/hover-hitbox'
import type { GLTFNodes } from '../types'

interface UseCameraFocusTargetsArgs {
  cameraFocusTargets?: string[]
  modelControl: ModelControlsInterface
  controlsRef: RefObject<OrbitControlsRefProps>
}

const useCameraFocusTargets = ({
  cameraFocusTargets,
  modelControl,
  controlsRef,
}: UseCameraFocusTargetsArgs) => {
  const [nodes, setNodes] = useState<GLTFNodes>()
  const hasCameraFocus = useRef(false)
  const cameraFocusKey = cameraFocusTargets?.join()

  useEffect(() => {
    if (!cameraFocusTargets || !nodes) {
      return
    }

    if (!cameraFocusTargets.length) {
      if (hasCameraFocus.current) {
        controlsRef.current?.slideCameraToInitialTarget()
      }
      hasCameraFocus.current = false
      return
    }

    const focusBox = new Box3()
    Object.entries(modelControl).forEach(([name, control]) => {
      if (
        control.id &&
        cameraFocusTargets.includes(control.id) &&
        nodes[name]
      ) {
        focusBox.expandByObject(nodes[name])
      }
    })

    if (focusBox.isEmpty()) {
      return
    }

    controlsRef.current?.slideCameraTo(focusBox.getCenter(new Vector3()))
    hasCameraFocus.current = true
  }, [cameraFocusKey, nodes])

  return setNodes
}

export default useCameraFocusTargets
